import React, { useState } from "react";
import { MessageSquareHeart, X, Star } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import PrivacyNotice from "@/components/PrivacyNotice";

const STORAGE_KEY = "folio_feedback_dismissed";

export default function FeedbackTab() {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Pick a rating first."); return; }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        rating, comment, email: email || null,
        page: window.location.pathname,
      });
      setDone(true);
      try {
        localStorage.setItem(STORAGE_KEY, "true");
      } catch (err) {
        console.warn("[feedback] cannot persist dismissed flag:", err);
      }
      setTimeout(() => {
        setOpen(false); setDone(false);
        setRating(0); setComment(""); setEmail("");
      }, 2200);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          data-testid="feedback-tab-trigger"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 bg-ink text-paper px-4 py-3 shadow-xl flex items-center gap-2 text-xs uppercase tracking-widest hover:bg-terracotta transition-colors"
          title="Share feedback"
        >
          <MessageSquareHeart className="w-4 h-4" strokeWidth={1.5} />
          <span className="hidden sm:inline">How's Folio?</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="feedback-panel"
          className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-md bg-paper border border-edge shadow-2xl"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-edge">
            <div className="label-eyebrow text-sage flex items-center gap-2">
              <MessageSquareHeart className="w-3.5 h-3.5" strokeWidth={1.5} />
              How's Folio?
            </div>
            <button
              data-testid="feedback-close"
              onClick={() => setOpen(false)}
              className="text-muted_ink hover:text-ink"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {done ? (
            <div data-testid="feedback-done" className="p-8 text-center">
              <div className="font-serif text-2xl">Thank you.</div>
              <p className="text-sm text-muted_ink mt-2">We read every one.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="p-5 space-y-4">
              <div>
                <div className="label-eyebrow mb-2">Rate your visit</div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`feedback-star-${n}`}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      onClick={() => setRating(n)}
                      className="p-1"
                    >
                      <Star
                        className={`w-6 h-6 transition-colors ${
                          (hover || rating) >= n ? "fill-terracotta text-terracotta" : "text-edge"
                        }`}
                        strokeWidth={1.25}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label-eyebrow block mb-2">What could be better? (optional)</label>
                <Textarea
                  data-testid="feedback-comment"
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Anything at all — copy, design, missing books, bugs…"
                  className="rounded-none border-edge focus-visible:ring-terracotta focus-visible:ring-1"
                  maxLength={2000}
                />
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Email (optional — only if you want a reply)</label>
                <Input
                  data-testid="feedback-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="rounded-none border-edge h-11 focus-visible:ring-terracotta focus-visible:ring-1"
                />
              </div>

              <button
                data-testid="feedback-submit"
                type="submit"
                disabled={submitting}
                className="btn-primary w-full disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send"}
              </button>
              <div className="text-[11px] text-muted_ink text-center">
                Sent anonymously unless you add an email.
              </div>
              <PrivacyNotice
                testid="feedback-privacy"
                purpose="If you leave an email, we'll use it to reply to your feedback."
              />
            </form>
          )}
        </div>
      )}
    </>
  );
}
